// src/pages/Dashboard/Admin/DetailsAdherents/components/CompetitionsPanel.jsx

import React, { useMemo } from "react";
import { FaExternalLinkAlt } from "react-icons/fa";

import DashboardDataTable from "./DashboardDataTable";

import {
  formatDate,
  getBooleanStatusClass,
  safeArray,
  stripHtml,
  toAbsoluteUrl,
} from "../utils/adherentDashboardUtils";

function Badge({ children, className }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${className}`}
    >
      {children}
    </span>
  );
}

function classementClass(classement) {
  const value = String(classement || "").toLowerCase();

  if (value === "1" || value.includes("1er") || value.includes("1ère")) {
    return "bg-yellow-50 text-yellow-700 ring-yellow-200 dark:bg-yellow-500/10 dark:text-yellow-300 dark:ring-yellow-500/20";
  }

  if (value === "2" || value.includes("2e")) {
    return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
  }

  if (value === "3" || value.includes("3e")) {
    return "bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20";
  }

  return "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20";
}

export default function CompetitionsPanel({ competitions }) {
  const rows = useMemo(() => {
    return safeArray(competitions).sort(
      (a, b) =>
        new Date(b?.start || b?.date_event || 0).getTime() -
        new Date(a?.start || a?.date_event || 0).getTime()
    );
  }, [competitions]);

  return (
    <DashboardDataTable
      minWidth="1150px"
      columns={[
        {
          key: "event",
          label: "Compétition",
          render: (row) => {
            const img = row?.photo_url ? toAbsoluteUrl(row.photo_url) : "";

            return (
              <div className="flex items-center gap-3">
                <div className="h-14 w-20 shrink-0 overflow-hidden rounded-2xl bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
                  {img ? (
                    <img
                      src={img}
                      alt={row?.titre || "Compétition"}
                      className="h-full w-full object-cover"
                    />
                  ) : null}
                </div>

                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 dark:text-white">
                    {row?.titre || "Sans titre"}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {row?.lieu_event || row?.lieu || row?.localisation || "—"}
                  </p>
                </div>
              </div>
            );
          },
        },
        {
          key: "date",
          label: "Date",
          render: (row) => (
            <div>
              <p>{formatDate(row?.start || row?.date_event, true)}</p>
              {row?.horaire_event ? (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {stripHtml(row.horaire_event)}
                </p>
              ) : null}
            </div>
          ),
        },
        {
          key: "type",
          label: "Type / niveau",
          render: (row) => (
            <div className="flex flex-wrap gap-1.5">
              {row?.type_event ? (
                <Badge className="bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20">
                  {row.type_event}
                </Badge>
              ) : null}

              {row?.level_event ? (
                <Badge className="bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200 dark:bg-fuchsia-500/10 dark:text-fuchsia-300 dark:ring-fuchsia-500/20">
                  {row.level_event}
                </Badge>
              ) : null}

              {row?.cate_event ? (
                <Badge className="bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                  {row.cate_event}
                </Badge>
              ) : null}
            </div>
          ),
        },
        {
          key: "validation",
          label: "Validation",
          render: (row) => {
            const ok = !!row?.AdherentEvent?.is_validate;

            return (
              <Badge className={getBooleanStatusClass(ok)}>
                {ok ? "Validé" : "Non validé"}
              </Badge>
            );
          },
        },
        {
          key: "classement",
          label: "Classement",
          render: (row) => {
            const classement = row?.AdherentEvent?.classement;

            return classement ? (
              <Badge className={classementClass(classement)}>
                {classement}
              </Badge>
            ) : (
              "—"
            );
          },
        },
        {
          key: "resultats",
          label: "Résultats",
          render: (row) => row?.AdherentEvent?.resultats || "—",
        },
        {
          key: "agenda",
          label: "Lien",
          render: (row) =>
            row?.agenda_url ? (
              <a
                href={row.agenda_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaExternalLinkAlt />
                Ouvrir
              </a>
            ) : (
              "—"
            ),
        },
      ]}
      rows={rows}
      emptyMessage="Aucune compétition trouvée pour cet adhérent."
    />
  );
}